import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { format } from 'date-fns';

import {
  GetCampaignByIdNoTagsGQL,
  EntitiesTotalCountQueryGQL,
  SearchActivityEventsGQL,
  GetAssignmentGroupsForCurrentUserGQL,
  GetActionsCountsGQL,
  ActivityEvent,
} from 'generated/graphql';
import { SimpleQueryReducer } from 'query-builder/core/simple-query-reducer';
import { RuleSet } from 'query-builder/query.interfaces';
import { DefaultRulesetService } from 'query-builder/core/get-default-ruleset.service';
import { switchMap } from 'rxjs/operators';

export interface DashboardData {
  totalEntitiesCount: number;
  notes: any[];
  flaggedCount: number;
  flaggedForCurrentUserCount: number;
  flaggedByUserCount: number;
  activities: ActivityEvent[];
  assignedTasksCount: number;
  totalTasksCount: number;
  nextTaskDueText: string;
}


@Injectable({
  providedIn: 'root'
})
export class CampaignDashboardService {
  constructor(
    private _getCampaignByIdNoTagsGQL: GetCampaignByIdNoTagsGQL,
    private _entitiesTotalCountQueryGQL: EntitiesTotalCountQueryGQL,
    private _searchActivityEventsGQL: SearchActivityEventsGQL,
    private _getAssignmentGroupsForCurrentUserGQL: GetAssignmentGroupsForCurrentUserGQL,
    private _getActionsCountsGQL: GetActionsCountsGQL,
    private _defaultRulesetService: DefaultRulesetService
  ) {}

  getDashboardData(campaignId: string): Observable<DashboardData> {
    // Campaign stats
    const campaign$ = this._getCampaignByIdNoTagsGQL
      .watch(
        { campaignId, withEntitiesCount: true },
        { fetchPolicy: 'cache-and-network' }
      )
      .valueChanges.pipe(
        filter((res: any) => !!res.data?.getCampaign),
        map((res: any) => ({
          count: res.data.getCampaign.entitiesCount ?? 0,
          notes: res.data.getCampaign.notes.nodes || [],
        }))
      );

    // Flagged by anyone
    const flagged$ = this._defaultRulesetService.getFlaggedEntitiesRuleset().pipe(
      switchMap(ruleSet => this._getCountByQuery(campaignId, ruleSet))
    );

    // Flagged for current user
    const flaggedForCurrentUser$ = this._defaultRulesetService.getFollowUpRuleset().pipe(
      switchMap(ruleSet => this._getCountByQuery(campaignId, ruleSet))
    );

    // Flagged by current user
    const flaggedByUser$ = this._defaultRulesetService.getFlaggedByYouRuleset().pipe(
      switchMap(ruleSet => this._getCountByQuery(campaignId, ruleSet))
    );

    // Last activity
    const activities$ = this._searchActivityEventsGQL
      .watch(
        { campaignId, first: 5 },
        { fetchPolicy: 'cache-and-network' }
      )
      .valueChanges.pipe(
        filter((res: any) => !!res.data?.searchActivityEvents),
        map((res: any) => {
          const edges = res.data.searchActivityEvents.edges;
          return !edges ? [] : edges.map(edge => edge.node);
        })
      );

    // Tasks
    const endDate = format(new Date(), 'yyyy-MM-dd');
    const assignments$ = this._getAssignmentGroupsForCurrentUserGQL
      .watch(
        { campaignId, endDate },
        { fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' }
      )
      .valueChanges.pipe(
        filter((res: any) => !!res.data?.getActionAssignmentGroupsForCurrentUser),
        map((res: any) => res.data.getActionAssignmentGroupsForCurrentUser || [])
      );

    const actions$ = this._getActionsCountsGQL
      .watch(
        { campaignId, endDate },
        { fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' }
      )
      .valueChanges.pipe(
        filter((res: any) => !!res.data?.getActions),
        map((res: any) => res.data.getActions || [])
      );

    // Combine all
    return combineLatest([
      campaign$,
      flagged$,
      flaggedForCurrentUser$,
      flaggedByUser$,
      activities$,
      assignments$,
      actions$,
    ]).pipe(
      map(([campaign, flagged, flaggedForCurrentUser, flaggedByUser, activities, assignments, actions]) => {
        const now = new Date();
        const futureDueDates = assignments
          .map((a: any) => a.action?.dueDate)
          .filter((d: string | null) => d && new Date(d) >= now)
          .sort();

        const nextTaskDueText = futureDueDates.length > 0
          ? `Next due in ${Math.ceil((new Date(futureDueDates[0]).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))} days`
          : 'No upcoming tasks';

        return {
          totalEntitiesCount: campaign.count,
          notes: campaign.notes,
          flaggedCount: flagged,
          flaggedForCurrentUserCount: flaggedForCurrentUser,
          flaggedByUserCount: flaggedByUser,
          activities: activities,
          assignedTasksCount: assignments.length,
          totalTasksCount: actions.length,
          nextTaskDueText,
        };
      })
    );
  }

  private _getCountByQuery(campaignId: string, ruleSet: RuleSet): Observable<number> {
    const reducedQuery = new SimpleQueryReducer(ruleSet).reduce();
    const queryBody = JSON.stringify(reducedQuery);

    return this._entitiesTotalCountQueryGQL
      .watch(
        { campaignId, body: queryBody },
        { fetchPolicy: 'cache-and-network' }
      )
      .valueChanges.pipe(
        filter((res: any) => !!res.data?.entitiesQuery),
        map((res: any) => res.data.entitiesQuery.totalCount || 0)
      );
  }
}
